import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useCustomSnackbar, CurrencyProvider } from '@procureai/ui-mui';
import { useEffect } from 'react';
import { DefaultValues, useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IntakeBudgetForm } from '../../IntakeBudgetStep/components/IntakeBudgetForm';
import { IntakeChatRequestDescriptionForm } from '../../IntakeChatRequestDescriptionStep/components/IntakeChatRequestDescriptionForm';
import { IntakeRequestDetailsForm } from '../../IntakeRequestDetailsStep/components/IntakeRequestDetailsForm';
import { IntakeRequestStepLineItems } from '../../IntakeRequestLineItemStep/components/IntakeRequestStepLineItems/IntakeRequestStepLineItems';
import {
  intakeRequestLineItemArraySchema,
  IntakeRequestLineItems,
} from '../../IntakeRequestLineItemStep/IntakeRequestLineItemStep.schema';
import {
  IntakeRequestComplexStepFormData,
  intakeRequestComplexStepFormDataDefault,
  intakeRequestComplexStepFormDataSchema,
} from '../IntakeRequestComplexStep.schema';
import { AstroUserProfile } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useGridValidator } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid.hooks';
import { usePrefillDeliveryLocation } from '~/routes/IntakeDetail/hooks/UsePrefillDeliveryLocation';
import { mergeOverrideNull } from '~/util/object';
export interface FormSubmitData extends IntakeRequestComplexStepFormData, IntakeRequestLineItems {}

interface IntakeRequestComplexStepFormProps {
  formId: string;
  data: DefaultValues<IntakeRequestComplexStepFormData> & IntakeRequestLineItems;
  profile: AstroUserProfile;
  onSubmit: (data: FormSubmitData) => Promise<void>;
  disabled?: boolean;
}

export const IntakeRequestComplexStepForm = ({
  formId,
  data,
  profile,
  onSubmit,
  disabled,
}: IntakeRequestComplexStepFormProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestComplexStep' });
  const { onError } = useCustomSnackbar();

  const apiRef = useGridApiRef();
  const { isValid } = useGridValidator({
    apiRef,
    schema: intakeRequestLineItemArraySchema,
  });

  const form = useForm<IntakeRequestComplexStepFormData>({
    mode: 'onTouched',
    resolver: zodResolver(intakeRequestComplexStepFormDataSchema),
    defaultValues: {
      ...intakeRequestComplexStepFormDataDefault,
      organizationId: profile.organizationId,
      accountingAreaId: profile.accountingAreaId,
      currency: profile.currency,
      deliveryLocationId: profile.deliveryLocationId,
    },
  });

  usePrefillDeliveryLocation(form);

  useEffect(() => {
    form.reset(
      mergeOverrideNull(data, {
        organizationId: profile.organizationId,
        accountingAreaId: profile.accountingAreaId,
        currency: profile.currency,
        deliveryLocationId: profile.deliveryLocationId,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const currency = useWatch({ control: form.control, name: 'currency' });
  const commodityGroupId = useWatch({ control: form.control, name: 'commodityGroupId' });

  const submit = (requestDetails: IntakeRequestComplexStepFormData) => {
    const { success, data: lineItems } = isValid();
    if (!success) {
      onError(t('error.lineItems'));
      return;
    }

    onSubmit({ ...requestDetails, lineItems });
  };

  return (
    <FormProvider {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(submit, (error) => {
          console.log(error);
        })}
        autoComplete="off"
        noValidate
      >
        <CurrencyProvider value={currency}>
          <Grid container spacing={4}>
            <IntakeRequestDetailsForm disabled={disabled} />
            <Grid size={12}>
              <IntakeBudgetForm disabled={disabled} />
            </Grid>
            <Grid size={12}>
              <IntakeChatRequestDescriptionForm disabled={disabled} pathPrefix="summary" />
            </Grid>
            <Grid size={12}>
              <IntakeRequestStepLineItems
                disabled={disabled}
                apiRef={apiRef}
                lineItems={data.lineItems}
                defaultValues={{ commodityGroupId }}
              />
            </Grid>
          </Grid>
        </CurrencyProvider>
      </form>
    </FormProvider>
  );
};
